import { MouseEvent, forwardRef, useMemo } from "react";

import PaypalBtnLogo from "chameleon/icons/paypal-btn-logo";
import PaypalBtnLogoInverse from "chameleon/icons/paypal-btn-logo-inverse";
import { LoadingSpinner } from "chameleon/lib/components";

import {
  blackVariantButtonStyles,
  blueVariantButtonStyles,
  buttonWrapper,
  goldVariantButtonStyles,
  paypalButtonClass,
  silverVariantButtonStyles,
  whiteVariantButtonStyles,
} from "./paypal-button.css";

export type PayPalButtonProps = {
  /**
   * Is the Button disabled?
   */
  isDisabled?: boolean;
  /**
   * Is the button loading?
   */
  isLoading?: boolean;
  /**
   * onclick handler
   */
  onClick?: (e?: MouseEvent) => void | unknown;
  /**
   * Text to display in the button
   */
  text?: string;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Position of the icon
   */
  position?: "left" | "right";
  /**
   * Test id will be applied if added
   */
  testId?: string;
  /**
   * Variant - default/gold
   */
  variant?: "default" | "blue" | "silver" | "white" | "black" | "gold";
};

/**
 * A basic button with a loading state
 */
export const PayPalButton = forwardRef<HTMLButtonElement, PayPalButtonProps>(
  (
    {
      isDisabled = false,
      isLoading = false,
      text = "",
      onClick,
      tabIndex,
      testId,
      position = "left",
      variant = "default",
    }: PayPalButtonProps,
    ref
  ) => {
    const variantStyles = useMemo(() => {
      switch (variant) {
        case "blue":
          return blueVariantButtonStyles;
        case "silver":
          return silverVariantButtonStyles;
        case "white":
          return whiteVariantButtonStyles;
        case "black":
          return blackVariantButtonStyles;
        default:
          return goldVariantButtonStyles;
      }
    }, [variant]);

    const svg = useMemo(() => {
      switch (variant) {
        case "blue":
        case "black":
          return <PaypalBtnLogoInverse sizeVariant={40} isRectangle />;
        default:
          return <PaypalBtnLogo sizeVariant={40} isRectangle />;
      }
    }, [variant]);

    return (
      <div className={buttonWrapper}>
        <button
          disabled={isDisabled}
          onClick={onClick}
          className={`${paypalButtonClass} ${variantStyles}`}
          tabIndex={tabIndex}
          ref={ref}
          data-testid={testId}
        >
          <>
            {position == "left" ? svg : undefined}
            {isLoading && <LoadingSpinner sizeVariant={24} />}
            {text}
            {position == "right" ? svg : undefined}
          </>
        </button>
      </div>
    );
  }
);

PayPalButton.displayName = "PayPalButton";
